import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import MainLayout from "../../layouts/MainLayout";
import Alert from "../../components/Alert";
import { listRoles } from "../../api/roleApi";
import { getPermissionsByRole, savePermissions } from "../../api/permissionApi";
import "./PermissionsPage.css";

function useQueryParam(name) {
  const { search } = useLocation();
  return new URLSearchParams(search).get(name);
}

/** Toggles a boolean field on one node inside the sidebar/nav/child tree, immutably. */
function toggleField(tree, targetId, level, field) {
  return tree.map((sidebarItem) => {
    if (level === "sidebar" && sidebarItem.id === targetId) {
      return { ...sidebarItem, [field]: !sidebarItem[field] };
    }
    if (!sidebarItem.navItems) {
      return sidebarItem;
    }
    return {
      ...sidebarItem,
      navItems: sidebarItem.navItems.map((navItem) => {
        if (level === "nav" && navItem.id === targetId) {
          return { ...navItem, [field]: !navItem[field] };
        }
        if (!navItem.childItems) {
          return navItem;
        }
        return {
          ...navItem,
          childItems: navItem.childItems.map((child) =>
            level === "child" && child.id === targetId ? { ...child, [field]: !child[field] } : child
          ),
        };
      }),
    };
  });
}

/** Maps the GET response shape to the POST request shape (id -> *ItemId). */
function toSaveRequest(roleId, tree) {
  return {
    roleId,
    sidebarItems: tree.map((sidebarItem) => ({
      sidebarItemId: sidebarItem.id,
      canView: sidebarItem.canView,
      navItems: (sidebarItem.navItems || []).map((navItem) => ({
        navItemId: navItem.id,
        canView: navItem.canView,
        childItems: (navItem.childItems || []).map((child) => ({
          childNavItemId: child.id,
          canView: child.canView,
          canAdd: child.canAdd,
          canEdit: child.canEdit,
          canDelete: child.canDelete,
        })),
      })),
    })),
  };
}

export default function PermissionsPage() {
  const roleIdFromQuery = useQueryParam("roleId");

  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState(roleIdFromQuery || "");
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    listRoles().then(setRoles).catch(() => setError("Failed to load roles."));
  }, []);

  useEffect(() => {
    if (!selectedRoleId) {
      setTree([]);
      return;
    }
    setLoading(true);
    setError("");
    getPermissionsByRole(selectedRoleId)
      .then(setTree)
      .catch(() => setError("Failed to load permissions for this role."))
      .finally(() => setLoading(false));
  }, [selectedRoleId]);

  const selectedRole = roles.find((r) => String(r.roleId) === String(selectedRoleId));
  const isSuperAdminRole = selectedRole && selectedRole.superAdminRole;

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await savePermissions(toSaveRequest(Number(selectedRoleId), tree));
      setSuccess("Permissions saved.");
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Role Permissions</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      <div className="role-picker">
        <label htmlFor="role-select">Role</label>
        <select id="role-select" value={selectedRoleId} onChange={(e) => setSelectedRoleId(e.target.value)}>
          <option value="">Select a role…</option>
          {roles.map((role) => (
            <option key={role.roleId} value={role.roleId} disabled={role.superAdminRole}>
              {role.roleName}
              {role.superAdminRole ? " (not editable)" : ""}
            </option>
          ))}
        </select>
      </div>

      {isSuperAdminRole && <Alert type="info">Super Admin has full access by default and cannot be edited.</Alert>}

      {loading && <p>Loading permissions…</p>}

      {!loading && selectedRoleId && !isSuperAdminRole && (
        <>
          <div className="permission-tree">
            {tree.map((sidebarItem) => {
              const flat = !sidebarItem.navItems || sidebarItem.navItems.length === 0;
              return (
                <div key={sidebarItem.id} className="permission-sidebar-block">
                  <label className="permission-row sidebar-row">
                    {flat && (
                      <input
                        type="checkbox"
                        checked={sidebarItem.canView}
                        onChange={() => setTree(toggleField(tree, sidebarItem.id, "sidebar", "canView"))}
                      />
                    )}
                    <strong>{sidebarItem.name}</strong>
                  </label>

                  {!flat &&
                    sidebarItem.navItems.map((navItem) => (
                      <div key={navItem.id} className="permission-nav-block">
                        <div className="permission-row nav-row">{navItem.name}</div>
                        <div className="permission-child-table">
                          {(navItem.childItems || []).map((child) => (
                            <div key={child.id} className="permission-child-row">
                              <span className="child-name">{child.name}</span>
                              {["canView", "canAdd", "canEdit", "canDelete"].map((field) => (
                                <label key={field} className="permission-flag">
                                  <input
                                    type="checkbox"
                                    checked={child[field]}
                                    onChange={() => setTree(toggleField(tree, child.id, "child", field))}
                                  />
                                  {field.replace("can", "")}
                                </label>
                              ))}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                </div>
              );
            })}
          </div>

          <div className="form-actions">
            <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Permissions"}
            </button>
          </div>
        </>
      )}
    </MainLayout>
  );
}
